/****************
 * WHAT AN ORDER ROW'S ACTION IS CALLED, IN ONE PLACE.
 *
 * Gianni, 2026-09-10: "The Order List of his session have 1 'Session_Stopped'
 * action". That is the database enum, rendered raw into a badge beside CLOSE
 * and INCREASE — which are also enums, but ones that happen to read as English.
 *
 * A session's life is written into the order feed on purpose: the notification
 * bell, its unread mark, the mobile list and the session's Orders tab all read
 * `replicated_orders`, so a lifecycle event reaches every one of them for free.
 * The cost is that four surfaces then have to know that SESSION_STOPPED is not
 * an order, and each of them decided separately. The bell said "session ended";
 * the Orders tab said SESSION_STOPPED.
 *
 * ── Why a map and not a ternary ──────────────────────────────────────────────
 *
 * The bell's version was `SESSION_STARTED ? 'session started' : 'session
 * ended'` — two events assumed to be all of them, with a fallback that
 * ASSERTED an ending rather than claiming nothing. So the third event told
 * users their session had ended when it had not: SESSION_SETTINGS_CHANGED
 * shipped on 2026-09-10 and announced a false ending to anyone who changed a
 * leverage cap on a running session.
 *
 * An unknown event now reads as "session updated", which is vague and true.
 * Adding one to recordLifecycle without adding it here costs precision, not
 * correctness.
 ****************/

import i18n from "@/i18n";
import { LIFECYCLE_LABEL_KEYS, actionLabelOf, isLifecycleAction } from "@/lib/notifications.core";

/*
 * The list of events and their keys lives in lib/notifications.core.ts since
 * 2026-10-08, byte-identical in the phone app, so both name every event the
 * same way and in the reader's language. The reasoning above still holds; it
 * now holds for both apps at once.
 */
export { isLifecycleAction };

/** Every lifecycle action the shared map names (a test iterates the real map). */
export const LIFECYCLE_ACTIONS = Object.keys(LIFECYCLE_LABEL_KEYS);

/**
 * The label for an order row's action.
 *
 * Trading actions (OPEN, CLOSE, INCREASE, DECREASE) are returned unchanged --
 * they are the words the rest of the product uses and a user reading a trade
 * blotter expects them.
 */
export function actionLabel(action?: string | null): string {
  const label = actionLabelOf(action);
  if (!label) return "—";
  return "key" in label ? i18n.t(`messages:${label.key}`) : label.text;
}
